// Display formatting helpers. Stat block math + age/dob/timestamp rendering.

export function formatToi(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatPlusMinus(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n > 0) return `+${n}`;
  return String(n);
}

export function formatSavePct(pct: number | null | undefined): string {
  if (pct == null) return '—';
  return pct.toFixed(3);
}

export function formatHeight(inches: number | null | undefined): string {
  if (!inches) return '—';
  const ft = Math.floor(inches / 12);
  const inch = inches % 12;
  return `${ft}'${inch}"`;
}

export function formatWeight(lb: number | null | undefined): string {
  if (!lb) return '—';
  return `${lb} lb`;
}

export function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const birth = new Date(`${dob}T00:00:00Z`);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - birth.getUTCFullYear();
  const m = now.getUTCMonth() - birth.getUTCMonth();
  if (m < 0 || (m === 0 && now.getUTCDate() < birth.getUTCDate())) age--;
  return age;
}

export function formatDob(dob: string | null | undefined): string {
  if (!dob) return '—';
  const d = new Date(`${dob}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function birthdayTouchpoint(dob: string | null | undefined): { label: string; when: string; daysUntil: number } | null {
  if (!dob) return null;
  const parsed = new Date(`${dob}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;

  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  let nextBirthday = new Date(Date.UTC(today.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
  if (nextBirthday.getTime() < today.getTime()) {
    nextBirthday = new Date(Date.UTC(today.getUTCFullYear() + 1, parsed.getUTCMonth(), parsed.getUTCDate()));
  }

  const daysUntil = Math.round((nextBirthday.getTime() - today.getTime()) / 86_400_000);
  if (daysUntil > 7) return null;
  if (daysUntil === 0) return { label: 'Birthday today', when: 'today', daysUntil };
  if (daysUntil === 1) return { label: 'Birthday tomorrow', when: 'tomorrow', daysUntil };
  return { label: `Birthday in ${daysUntil}d`, when: `in ${daysUntil} days`, daysUntil };
}

export function formatGameDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function classificationLabel(c: 'pro_men' | 'pro_women' | 'prospect'): string {
  if (c === 'pro_men') return 'Pro Men';
  if (c === 'pro_women') return 'Pro Women';
  return 'Prospect';
}

export function priorityLabel(p: number | null | undefined): string {
  if (p == null) return '';
  return `P${p}`;
}

export function daysFromTodayLabel(iso: string | null | undefined): {
  label: string;
  days: number | null;
} {
  if (!iso) return { label: 'TBD', days: null };
  const target = new Date(`${iso}T00:00:00Z`);
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return { label: 'today', days: 0 };
  if (diff === 1) return { label: 'tomorrow', days: 1 };
  if (diff < 0) return { label: `${-diff}d ago`, days: diff };
  if (diff <= 14) return { label: `${diff}d`, days: diff };
  return { label: `${diff}d`, days: diff };
}
